import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import type { ApiError } from "@/react/config/CustomErrors";
import type { Comment } from "@/react/types/Comment";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { COMMENTS_BASE_QUERY_KEY } from "../CommentsList/getCommentsListKey";

const COMMENTS_HIERARCHY_QUERY_KEY = "hierarchy";

export const getCommentHierarchyKey = ({
  postId,
  id,
}: {
  postId?: number | null;
  id: string | number;
}) => [
  COMMENTS_BASE_QUERY_KEY,
  postId,
  COMMENTS_HIERARCHY_QUERY_KEY,
  String(id),
];

export const useCommentHierarchy = ({
  postId,
  id,
  postCommentsClosed,
  postLikingDisabled,
}: {
  postId: number;
  id: number;
  postCommentsClosed: boolean;
  postLikingDisabled: boolean;
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => getCommentHierarchyKey({ postId, id }),
    [postId, id],
  );

  useEffect(() => {
    queryClient.removeQueries(queryKey);
  }, [postCommentsClosed, postLikingDisabled, queryClient, queryKey]);

  // `cacheTime: 0` ensures that comments are always fully reloaded on every
  // mount, i.e., whenever the comment section is opened. This is done to cope
  // with the current comment highlighting and anchor link behavior.
  const { data: comment, isLoading } = useQuery(
    queryKey,
    () =>
      reactQueryGet(
        internalApi.comments.commentHierarchy({
          postId: postId,
          id: id ?? undefined,
        }),
      ),
    {
      cacheTime: 0,
      onError: (error: ApiError) => {
        if (error.status === 404) {
          queryClient.setQueryData(queryKey, null);
        }
      },
      retry(failureCount, error: ApiError) {
        // When moderators reject a reported comment, the comment is soft-deleted
        // and the refetch returns a 404. We don't want to retry in this case.
        if (error.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
    },
  );

  const updateQueryData = (commentData: Comment) =>
    queryClient.setQueryData(queryKey, {
      ...commentData,
      replies: comment.replies,
    });

  return {
    comment,
    isLoading,
    updateQueryData,
  };
};
